import type { RouteMaster } from '../../../data/routes';
import {
  MAX_CONNECTING_ROUTE_IDS,
  resolveConnectingSearchRouteIds,
} from '../../../utils/broadcastContents';

/**
 * 接続線ブロックの「遅延を検索する線区」選択 UI (プルダウンは 1 つ)。
 *
 * 接続線遅延 (乗換駅ベース / part2) の `lines[].route_id` (先頭) と
 * `lines[].route_ids` (2 件目以降) を、**1 つの一覧 + 1 つのプルダウン** で編集する
 * (`ConnectingLineBlock.tsx` から利用)。
 *
 * ## 何のための項目か
 *
 * 1 つの接続線ブロックで **複数の線区** を検索し、放送側 (UpdateContents) は
 * 1 つの案内文を生成する。乗り換え列車の遅延 (`transfer_aware`) / 発車見込みは
 * **発車が最も早い接続列車**、接続線全体 (`whole_line`) などは **最大遅延** を採る。
 * 例) 大宮の高崎線下りは「東北下り」と「東北貨物下り」の遅れの影響を受けるため
 *     「高崎線」「東北」「東北貨」を選ぶ。
 *
 * ## 一覧の意味 (重要)
 *
 * **検索対象 = 一覧の全件**。画面上は先頭とそれ以外を区別せず、同じ見た目で並べる。
 * 保存形式は従来どおり `route_id` = 先頭 / `route_ids` = 2 件目以降で、
 * 放送側 `_extract_connecting_route_ids` の規則 (route_id + route_ids) と一致する。
 * 先頭は放送文の `{route_id}` / 線名の解決 / アイステイションズ文面 / ログにも使われるが、
 * 運用者が意識する必要はないため、「メイン」「追加」の区別や入れ替え操作は画面に出さない。
 *   - プルダウンで選んだ線区は一覧の末尾に加わる (一覧が空なら先頭になる)。
 *   - 先頭を削除すると、次の線区が先頭に繰り上がる。
 *   - 先頭を変えたいときは、削除して選び直す。
 *
 * `route_ids` 未設定 (空配列 → キーごと省略) なら `route_id` のみを検索するため、
 * 既存データの放送文は 1 バイトも変わらない (後方互換)。
 * `route_ids` に `route_id` と同じ値が入っている既存データは、一覧では 1 件に畳んで表示する。
 *
 * `routes-master.json` との整合はサーバ側でも強制していない。
 * マスタ未登録の値が既存データに入っていても **画面から消さない**
 * (Core-02 以前の既存データや Transformer 先行分を壊さないため)。
 */
export function ConnectingRouteIdsSelect({
  testIdPrefix,
  ariaLabelPrefix,
  routes,
  routeId,
  routeIds,
  maxItems,
  selectTestId,
  selectInvalid = false,
  invalid = false,
  onChange,
}: {
  /**
   * `data-testid` の接頭辞。接続線ブロックごとに一意になるよう呼び出し側で
   * index を含めた値を渡す (例: `connecting-line-route-ids-0`)。
   */
  testIdPrefix: string;
  /**
   * `aria-label` の接頭辞。どの接続線に属する選択欄かを区別できる文言を渡す
   * (例: `接続線#1 `)。
   */
  ariaLabelPrefix?: string;
  /** 選択肢 (ATOS 線区マスタ)。取得失敗時は空配列でよい。 */
  routes: readonly RouteMaster[];
  /** 先頭の route_id (`lines[].route_id`)。未選択なら空文字 / undefined。 */
  routeId: string | undefined;
  /** 2 件目以降の route_id 一覧 (`lines[].route_ids`。順序は入力順 = 遅延取得順)。 */
  routeIds: readonly string[];
  /**
   * `route_ids` の上限件数 (先頭の `route_id` は含めない)。既定は {@link MAX_CONNECTING_ROUTE_IDS} で、サーバ側
   * `MAX_CONNECTING_ROUTE_IDS` (`lambda/control-panel-api/index.py`) と一致する
   * (上限に達したらプルダウンを disabled にして 400 を事前に防ぐ)。
   */
  maxItems?: number;
  /**
   * プルダウンの `data-testid`。「線区が未選択」のバリデーションエラーが指す要素に
   * するため、呼び出し側から指定できる。省略時は `${testIdPrefix}-add-select`。
   */
  selectTestId?: string;
  /** 線区が未選択のまま保存しようとしたとき、プルダウンを赤枠にするか。 */
  selectInvalid?: boolean;
  /** 入力エラー (重複 / 上限超過など) で枠全体を赤枠にするか。 */
  invalid?: boolean;
  /**
   * 一覧が変わったときに呼ぶ。`route_id` と `route_ids` は常にセットで返す
   * (先頭の繰り上がりを 1 回の更新で行い、中間状態を作らないため)。
   * `routeIds` は空配列のことがある (呼び出し側で undefined に落とす)。
   */
  onChange: (next: { routeId: string; routeIds: string[] }) => void;
}): React.JSX.Element {
  const limit = maxItems ?? MAX_CONNECTING_ROUTE_IDS;

  // 実際に遅延を検索する線区 (route_id + route_ids / 重複排除済み)。放送側と同じ規則。
  // 既存データ / import された JSON が壊れていても落ちないよう、実行時にも型を確認する。
  const list = resolveConnectingSearchRouteIds({
    route_id: routeId,
    route_ids: Array.isArray(routeIds) ? routeIds : [],
  });
  const primary = list[0];
  // 上限は route_ids (2 件目以降) の件数。先頭の route_id ぶんを足したものが検索できる最大件数になる。
  const maxTotal = limit + 1;
  const isFull = list.length >= maxTotal;

  // 選択済みの線区はプルダウンから除外する (重複はサーバ側で 400 になる)。
  const selectedIds = new Set(list);
  const addableRoutes = routes.filter((r) => !selectedIds.has(r.route_id));

  /**
   * 線区の表示名をマスタの線名 → route_id の順で解決する。
   * route_id は運用者に不要な内部値なので、マスタに名称がある限り画面には出さない。
   */
  const displayNameOf = (id: string): string =>
    routes.find((r) => r.route_id === id)?.route_name || id;

  /** 一覧 (先頭 = route_id) を route_id + route_ids に分けて通知する。 */
  const emit = (nextList: string[]): void => {
    onChange({ routeId: nextList[0] ?? '', routeIds: nextList.slice(1) });
  };

  /** プルダウンで選んだ線区を末尾に加える。 */
  const addRoute = (id: string): void => {
    if (!id || selectedIds.has(id) || isFull) return;
    emit([...list, id]);
  };

  /** index 番目を取り除く (先頭を消すと次の線区が先頭になる)。 */
  const removeAt = (index: number): void => {
    emit(list.filter((_, i) => i !== index));
  };

  /** 1 行ぶんの表示 (名称 + マスタ未登録の注記)。 */
  const renderName = (id: string): React.JSX.Element => (
    <span className="connecting-route-list__name">
      {displayNameOf(id)}
      {routes.some((r) => r.route_id === id) ? null : (
        // マスタから削除された線区でも既存の設定値が画面から消えないようにする。
        <span className="connecting-route-list__warn">(一覧にない線区)</span>
      )}
    </span>
  );

  return (
    <div
      className={`connecting-route-ids-select connecting-section${invalid ? ' is-invalid' : ''}`}
      data-testid={testIdPrefix}
    >
      <div className="connecting-section__title">遅延を検索する線区</div>
      <p className="connecting-section__lead">
        選んだ線区の遅延をまとめて検索します。1 つ以上の選択が必須です。
        最初に選んだ線区の線名が、線区愛称に自動入力されます。
      </p>

      {/* 検索する線区の一覧。先頭とそれ以外は画面上では区別しない。 */}
      <ul className="connecting-route-list">
        {list.map((id, i) => (
          <li key={`${id}-${i}`} data-testid={`${testIdPrefix}-item-${i}`}>
            {renderName(id)}
            <button
              type="button"
              className="danger connecting-route-list__remove"
              aria-label={`${ariaLabelPrefix ?? ''}遅延を検索する線区 ${displayNameOf(id)} を削除`}
              data-testid={`${testIdPrefix}-remove-${i}`}
              onClick={() => removeAt(i)}
            >
              × 削除
            </button>
          </li>
        ))}
      </ul>

      {primary === undefined ? (
        <div className="connecting-section__empty" data-testid={`${testIdPrefix}-empty`}>
          まだ線区が選ばれていません。遅延を検索する線区を選んでください。
        </div>
      ) : list.length === 1 ? (
        <div className="connecting-section__empty" data-testid={`${testIdPrefix}-empty`}>
          {displayNameOf(primary)}
          の遅延のみを検索します。影響を受ける他の線区があれば追加してください（任意）。
        </div>
      ) : null}

      {/* プルダウンは 1 つだけ。選んだ線区は一覧の末尾に加わる。 */}
      <select
        aria-label={`${ariaLabelPrefix ?? ''}遅延を検索する線区を選択`}
        data-testid={selectTestId ?? `${testIdPrefix}-add-select`}
        className={selectInvalid ? 'is-invalid' : ''}
        value=""
        disabled={isFull || addableRoutes.length === 0}
        onChange={(e) => addRoute(e.target.value)}
      >
        <option value="">
          {isFull
            ? `(選べる線区は上限 ${maxTotal} 件に達しています)`
            : addableRoutes.length === 0
              ? '(選べる線区がありません)'
              : primary === undefined
                ? '(線区を選択)'
                : '(線区を追加)'}
        </option>
        {addableRoutes.map((r) => (
          <option key={r.route_id} value={r.route_id}>
            {r.route_name || r.route_id}
          </option>
        ))}
      </select>

      <details className="connecting-section__details">
        <summary>検索の仕組みを見る</summary>
        <p className="hint">
          一覧の線区のうち <strong>最大の遅れで 1 つの案内文</strong>
          を生成します（同じ線区を二重に案内することはありません）。選べるのは最大 {maxTotal}{' '}
          件までです。
          <br />
          例）大宮の高崎線下りは「東北下り」と「東北貨物下り」の遅れの影響を受けるため、
          「高崎線」「東北」「東北貨」を選びます。
        </p>
      </details>
    </div>
  );
}
